// Sync layout readers derive the managed opt-in from committed stores at call
// time, so a discarded render or a signed-out account cannot leak into them.
// FLAG_CLEANUP: delete when both layout flags reach 100% and sync readers retire.
import { LABS_FEATURE } from '@/app-shell/settings/heidi-labs/early-access/labs-feature-ids';
import {
  isSessionLayoutManagedReleaseEnabled,
  SESSION_LAYOUT_MANAGED_RELEASE_FLAG_KEY,
} from '@heidi/session/flags';

type LabsOverridesUser = {
  scribe_session_page_v2_enabled?: boolean;
  session_settings?: { labs_feature_overrides?: Record<string, boolean> } | null;
};

// Required at call time, like the cached-user reads in `legacy-flags.ts`, so
// this near-leaf module keeps the replay store and user context off its load graph.
/* eslint-disable @typescript-eslint/no-require-imports */
const isManagedReleaseReplayed = (): boolean => {
  const { readCachedFlagFallback } =
    require('@/app-shell/flags/offline-cached-flag-decisions') as typeof import('@/app-shell/flags/offline-cached-flag-decisions');
  return readCachedFlagFallback(SESSION_LAYOUT_MANAGED_RELEASE_FLAG_KEY);
};

// The same projection the hook gates read, so an auth-rejected refetch drops the opt-in here too.
const isLayoutOptInCommitted = (): boolean => {
  const { readCurrentUserQueryResult } =
    require('@heidi/workspace/data') as typeof import('@heidi/workspace/data');
  const { data } = readCurrentUserQueryResult();
  const user = data as LabsOverridesUser | undefined;
  const overrides = user?.session_settings?.labs_feature_overrides;
  return (
    overrides?.[LABS_FEATURE.sessionLayoutV2] ??
    user?.scribe_session_page_v2_enabled === true
  );
};
/* eslint-enable @typescript-eslint/no-require-imports */

/** The managed flag (live or replayed) AND committed layout enrolment, read now. */
export const readManagedReleaseLayoutOptedIn = (): boolean => {
  const isManagedReleaseEnabled =
    isSessionLayoutManagedReleaseEnabled() || isManagedReleaseReplayed();
  return isManagedReleaseEnabled && isLayoutOptInCommitted();
};
